import { useEffect, useRef, useState } from 'react';
import { applyPatch, type Operation } from 'fast-json-patch';
import type {
  ObserverRole,
  WheelObservableState,
} from '@goodtime/shared';
import { subscribeToWheelRoom, type ReactionEvent } from '../api/wheelStream';
import { authHeaders, ORIGIN } from '../api/client';

export type WheelStreamStatus =
  | 'idle'        // No room id yet
  | 'connecting'  // EventSource open, no state_init yet
  | 'live'        // Got state_init; receiving deltas
  | 'closed'      // Server sent {@code close} (failover) — auto-reconnects
  | 'error';      // Connection or parse error

/** A reaction received from SSE, decorated with a unique client-side id
 *  so consumers can use it as a React key and avoid re-rendering the
 *  same float overlay twice. */
export interface ReceivedReaction extends ReactionEvent {
  /** Client-assigned monotonic id — used as a React key for the float
   *  overlay. Stable for the lifetime of the hook. */
  clientId: number;
}

/** A seat-join toast received from SSE — a human took a bot's seat.
 *  Decorated with a client id for use as a React key + de-dup. The
 *  local player's own join is filtered out before this is produced. */
export interface SeatJoinToast {
  clientId: number;
  /** Joiner's display name (may be null/empty → component falls back). */
  playerName: string | null;
  /** Displaced bot's display name, e.g. "Brave Bea". */
  botName: string;
  emittedAtMs: number;
}

export interface WheelStreamConnection {
  /** Cached observable state. {@code null} until the first state_init
   * arrives, or after a connection error wipes the cache. */
  state: WheelObservableState | null;
  status: WheelStreamStatus;
  /** Human-readable last-error string for display. {@code null} when
   * not in {@code 'error'} status. */
  errorMessage: string | null;
  /** Most recent ~10 reactions (FIFO ring buffer). Consumers filter by
   *  {@code seatId} to overlay floats onto the matching seat avatar,
   *  using {@code clientId} as React key + animation lifecycle anchor.
   *  Entries auto-expire from the buffer when newer ones push them
   *  out — no explicit cleanup needed for the float component to
   *  finish its animation. */
  reactions: ReceivedReaction[];
  /** Recent seat-join toasts ("X took Y's seat"), newest last. The
   *  local player's own join is already filtered out. The toast
   *  component renders these and expires them by age. */
  seatToasts: SeatJoinToast[];
}

/**
 * Subscribe to a Wheel-of-Fortune room's SSE state stream and
 * maintain a typed, patched-in-place {@link WheelObservableState}.
 *
 * <p>Three event handlers cover the wire protocol:
 * <ul>
 *   <li>{@code state_init} replaces the cached state wholesale
 *       (full snapshot — used on connect, reconnect, and after a
 *       failover {@code close}).
 *   <li>{@code state_delta} applies an RFC-6902 patch to the cache
 *       via {@link applyPatch} from {@code fast-json-patch}.
 *   <li>{@code close} marks the status as {@code 'closed'}; the
 *       browser's EventSource auto-reconnects and the next
 *       {@code state_init} resyncs.
 * </ul>
 *
 * <p>Pass {@code null} as {@code roomId} to put the hook in
 * {@code 'idle'} — useful when the cast view is mounted before a
 * room is selected.
 *
 * @param roomId   UUID of the wheel room, or null to disconnect
 * @param role     observer role; defaults to {@code 'AUDIENCE'}
 * @param playerId required when {@code role === 'PLAYER'}
 */
/** How often the player sends a liveness ping while connected. Paired
 *  with the server's 30s stale threshold in {@code RoomIdleSweeper} —
 *  10s gives two retries of headroom before a seat gets evicted. */
const HEARTBEAT_INTERVAL_MS = 10_000;

export function useWheelRoomStream(
  roomId: string | null,
  role: ObserverRole = 'AUDIENCE',
  playerId?: string,
): WheelStreamConnection {
  const [state, setState] = useState<WheelObservableState | null>(null);
  const [status, setStatus] = useState<WheelStreamStatus>(
    roomId ? 'connecting' : 'idle',
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // R1.4 — recent reactions, FIFO-capped at REACTION_BUFFER_SIZE.
  const [reactions, setReactions] = useState<ReceivedReaction[]>([]);
  // Social — recent "X took Y's seat" toasts, FIFO-capped. Self-join
  // already filtered out in the onSeatTaken handler below.
  const [seatToasts, setSeatToasts] = useState<SeatJoinToast[]>([]);
  // Monotonic counter for client-side React keys. Kept in a ref so each
  // reaction gets a unique id even when multiple arrive in the same
  // render frame; setState's batching would otherwise hand out duplicates.
  const nextClientIdRef = useRef<number>(1);

  // Hold the cached state in a ref so the delta handler always reads
  // the latest value without re-subscribing on every state change.
  const stateRef = useRef<WheelObservableState | null>(null);

  // Heartbeat loop — runs only when this client owns a seat
  // (role === 'PLAYER'). Server treats 30s of silence as a ghost
  // player and synthesises a LEAVE_ROOM. Backgrounding the app
  // pauses these timers; that's the desired behaviour.
  useEffect(() => {
    if (!roomId || role !== 'PLAYER' || !playerId) return;

    const ping = (): void => {
      const url = `${ORIGIN}/api/rooms/${encodeURIComponent(roomId)}/heartbeat?playerId=${encodeURIComponent(playerId)}`;
      void authHeaders()
        .then((headers) => fetch(url, { method: 'POST', headers }))
        .catch(() => {
        // Heartbeat is fire-and-forget. Network blips shouldn't spam
        // the console; the sweeper's grace window absorbs them.
      });
    };

    ping();
    const handle = window.setInterval(ping, HEARTBEAT_INTERVAL_MS);
    return () => {
      window.clearInterval(handle);
    };
  }, [roomId, role, playerId]);

  useEffect(() => {
    if (!roomId) {
      setStatus('idle');
      setState(null);
      setErrorMessage(null);
      stateRef.current = null;
      return;
    }

    setStatus('connecting');
    setErrorMessage(null);

    const es = subscribeToWheelRoom(roomId, role, {
      onInit: (view) => {
        stateRef.current = view;
        setState(view);
        setStatus('live');
        setErrorMessage(null);
      },
      onDelta: (patch) => {
        const prev = stateRef.current;
        if (!prev) {
          // Should never happen — server sends state_init before any
          // state_delta. If it does, drop the patch and wait for the
          // next state_init.
          console.warn('[useWheelRoomStream] delta before init; dropping');
          return;
        }
        try {
          // applyPatch mutates a deep clone — clone first so React
          // sees a new object and re-renders.
          const next = structuredClone(prev) as WheelObservableState;
          applyPatch(next, patch as Operation[], /* validate */ false);
          stateRef.current = next;
          setState(next);
        } catch (e) {
          console.error('[useWheelRoomStream] applyPatch failed', e, patch);
          setStatus('error');
          setErrorMessage('Failed to apply state delta — reconnecting');
        }
      },
      onReaction: (event) => {
        // Push to the ring buffer; cap at REACTION_BUFFER_SIZE so a
        // long-lived match doesn't accumulate reactions forever.
        // Each reaction gets a unique clientId so overlays don't
        // re-render the same float as a different one.
        setReactions((prev) => {
          const next = prev.concat([{
            ...event,
            clientId: nextClientIdRef.current++,
          }]);
          if (next.length <= REACTION_BUFFER_SIZE) return next;
          return next.slice(next.length - REACTION_BUFFER_SIZE);
        });
      },
      onSeatTaken: (event) => {
        // Suppress your own join — you tapped it, you know you sat down.
        // Show only OTHER humans arriving (the Social payoff). Audience
        // mode (no local playerId) sees every join.
        if (playerId && event.playerId === playerId) return;
        setSeatToasts((prev) => {
          const next = prev.concat([{
            clientId: nextClientIdRef.current++,
            playerName: event.playerName,
            botName: event.botName,
            emittedAtMs: event.emittedAtMs,
          }]);
          if (next.length <= SEAT_TOAST_BUFFER_SIZE) return next;
          return next.slice(next.length - SEAT_TOAST_BUFFER_SIZE);
        });
      },
      onClose: () => {
        setStatus('closed');
      },
      onError: (event) => {
        // EventSource auto-reconnects after a transient blip; surface
        // the status so callers can show a "reconnecting…" indicator.
        // A persistent error stays in 'error' until the next state_init.
        if (es.readyState === EventSource.CLOSED) {
          setStatus('error');
          setErrorMessage('Connection closed');
        } else {
          setStatus('connecting');
          setErrorMessage(null);
        }
        // event is unused beyond status flip; logged for diagnostics.
        if (import.meta.env.DEV) {
          console.debug('[useWheelRoomStream] error event', event);
        }
      },
    }, playerId);

    return () => {
      es.close();
    };
  }, [roomId, role, playerId]);

  return { state, status, errorMessage, reactions, seatToasts };
}

/** Cap on the in-memory reaction buffer. The float overlay's animation
 *  lifetime is ~1.5s; at 10 entries we cover ~5 reactions/sec for ~2s
 *  before older entries roll off. Bumping this past ~32 wastes RAM
 *  without buying visible benefit. */
const REACTION_BUFFER_SIZE = 10;

/** Cap on the seat-join toast buffer. Joins are infrequent and a toast
 *  lives ~3.5s; 4 covers a burst of friends arriving at once without
 *  unbounded growth. */
const SEAT_TOAST_BUFFER_SIZE = 4;
